import { useCallback, useMemo, useEffect, useLayoutEffect, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import Icon from "./Icon";
import { useConfirmDialog } from "../context/ConfirmDialogContext";
import { useNavigationGuard } from "../hooks/useNavigationGuard";
import { Download } from "lucide-react";
import { Link } from "react-router-dom";

export default function ImageRegenerationModal({
  imageUrl,
  onClose,
  onRegenerate,
  isLoading,
  confirmImage,
  selectedFile,
  onFileSave,
  allGeneration = [],
  modifyMode,
  setModify,
  generationAmounts,
  useLogo,
  setUseLogo,
  useTheme,
  setUseTheme,
  logoUrl,
  themeUrl,
  prompt,
  setPrompt,
  hasOutput: hasOutputProp = false,
  user,
}: any) {
  const [activeImage, setActiveImage] = useState(imageUrl);
  const { t } = useTranslation();
  const { showConfirm, closeConfirm } = useConfirmDialog();
  const mainPrevStylesRef = useRef<any>(null);
  const prevViewportHeightRef = useRef<number | null>(null);
  const hasOutput = hasOutputProp || !!imageUrl || !!activeImage;
  
  // Check if there's active regeneration or unsaved changes
  const hasActiveOperation = useMemo(() => {
    return (
      isLoading ||
      hasOutput ||
      (!!prompt && prompt.trim().length > 0) ||
      (allGeneration?.length ?? 0) > 0
    );
  }, [isLoading, hasOutput, prompt, allGeneration?.length]);

  useNavigationGuard({
    isActive: hasActiveOperation,
    title: t("confirm_navigation") || "Confirm Navigation",
    message: isLoading
      ? t("image_regeneration_in_progress") ||
        "Image regeneration in progress. Are you sure you want to leave?"
      : t("unsaved_changes_warning") ||
        "You have unsaved changes. Are you sure you want to leave?",
    onConfirm: () => {
      onClose();
    },
  });

  const requestClose = useCallback(() => {
    if (!hasActiveOperation) {
      onClose();
      return;
    }

    showConfirm(
      t("confirm_navigation") || "Confirm",
      isLoading
        ? t("image_regeneration_in_progress") ||
            "Image regeneration in progress. Are you sure you want to leave?"
        : t("unsaved_changes_warning") ||
            "You have unsaved changes. Are you sure you want to leave?",
      () => {
        closeConfirm();
        onClose();
      }
    );
  }, [hasActiveOperation, isLoading, onClose, showConfirm, closeConfirm, t]);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") requestClose();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [requestClose]);

  useEffect(() => {
    if (imageUrl) {
      setActiveImage(imageUrl);
    }
  }, [imageUrl]);

  // Detect virtual keyboard close (viewport height increases) and scroll main back to top
  useEffect(() => {
    const getHeight = () => (window.visualViewport ? window.visualViewport.height : window.innerHeight);
    prevViewportHeightRef.current = getHeight();

    const onViewportResize = () => {
      const h = getHeight();
      const prev = prevViewportHeightRef.current ?? h;
      // If viewport height increased significantly, keyboard likely closed
      if (h > prev + 80) {
        const main = document.getElementById("mainContent") as HTMLElement | null;
        if (main) {
          try {
            main.scrollTo?.(0, 0);
            main.scrollTop = 0;
            window.scrollTo?.({ top: 0 });
          } catch (e) {}
        }
      }
      prevViewportHeightRef.current = h;
    };

    if ((window as any).visualViewport) {
      (window as any).visualViewport.addEventListener("resize", onViewportResize);
      return () => (window as any).visualViewport.removeEventListener("resize", onViewportResize);
    }

    window.addEventListener("resize", onViewportResize);
    return () => window.removeEventListener("resize", onViewportResize);
  }, []);

  // When modal opens: scroll main content to top and disable background scrolling.
  useLayoutEffect(() => {
    const mainEl = document.getElementById("mainContent") as HTMLElement | null;
    const prev: any = {};

    if (mainEl) {
      prev.overflow = mainEl.style.overflow;
      prev.scrollTop = mainEl.scrollTop;
      prev.position = mainEl.style.position;
      prev.top = mainEl.style.top;
      window.scrollTo({ top: 0 });

      // Try several ways to ensure the main container is at the top before paint
      try {
        mainEl.scrollTop = 0;
        if (mainEl.scrollTo) mainEl.scrollTo(0, 0);
        requestAnimationFrame(() => {
          try { mainEl.scrollTo(0, 0); } catch (e) {}
        });
        setTimeout(() => { try { mainEl.scrollTo(0, 0); } catch (e) {} }, 0);
        setTimeout(() => { try { mainEl.scrollTo(0, 0); } catch (e) {} }, 50);
      } catch (e) {}

      mainEl.style.overflow = "hidden";
    }

    // Also prevent body scrolling as a fallback (mobile browsers)
    prev.bodyOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    mainPrevStylesRef.current = prev;

    return () => {
      const prevStyles = mainPrevStylesRef.current || {};
      const main = document.getElementById("mainContent") as HTMLElement | null;
      if (main) {
        if (prevStyles.overflow !== undefined) main.style.overflow = prevStyles.overflow;
        if (prevStyles.position !== undefined) main.style.position = prevStyles.position;
        if (prevStyles.top !== undefined) main.style.top = prevStyles.top;
        if (typeof prevStyles.scrollTop === "number") main.scrollTop = prevStyles.scrollTop;
      }

      if (prevStyles.bodyOverflow !== undefined) {
        document.body.style.overflow = prevStyles.bodyOverflow;
      } else {
        document.body.style.overflow = "";
      }
    };
  }, []);

  const handleModifyToggle = (e: React.ChangeEvent<HTMLInputElement>) => {
    setModify(e.target.checked);
  };

  const blobUrlToBase64 = async (blobUrl: string): Promise<string> => {
    try {
      const response = await fetch(blobUrl);
      const blob = await response.blob();

      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result as string);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
    } catch (error) {
      console.error("Error converting blob URL to base64:", error);
      return blobUrl;
    }
  };

  const isBlobUrl = (url: string): boolean => {
    return url?.startsWith("blob:");
  };

  const handleSubmit = async () => {
    if (!prompt?.trim()) return;

    let payload = null;
    if (modifyMode && activeImage) {
      if (isBlobUrl(activeImage)) {
        payload = await blobUrlToBase64(activeImage);
      } else {
        payload = activeImage;
      }
    }

    onRegenerate(prompt, payload);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && e.ctrlKey) {
      handleSubmit();
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-white">
      <div className="absolute inset-0  flex flex-col md:justify-center items-center">
        {/* Content */}
        <div className="min-h-0 overflow-hidden lg:h-screen lg:max-h-screen">
          <div className="h-full grid grid-cols-1 lg:grid-cols-[1.2fr_0.8fr] grid-rows-[minmax(0,1fr)_auto] lg:grid-rows-1">
            {/* Left: Preview - Mobile में छोटा */}
            <div className="min-h-0 overflow-hidden p-2 sm:p-6 flex flex-col h-full">
              <div className="max-h-[45vh] sm:max-h-none rounded-md border border-gray-200 bg-gray-50 overflow-hidden shadow-sm relative group">
                {activeImage ? (
                <>
  {/* MAIN IMAGE */}
  <div
    className="
      h-full w-full flex items-center justify-center 
      p-3 sm:p-4 
      pb-24 sm:pb-4 
      relative
    "
  >
    <img
      src={activeImage}
      alt="Generated preview"
      className="w-full h-full object-contain"
    />

    {/* DOWNLOAD BUTTON */}
    <button
      onClick={() => {
        const link = document.createElement("a");
        link.href = activeImage;
        link.download = `image-${Date.now()}.png`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }}
      className="
        absolute top-3 right-3 sm:top-4 sm:right-4 
        p-2 rounded-md 
        hover:bg-[#d7d7fc] 
        border border-purple-600 
        flex items-center justify-center 
        text-base font-semibold text-[#7650e3] 
        transition-colors duration-200
      "
    >
      <Download className="w-5 h-5" />
    </button>
  </div>

  {/* MOBILE THUMBNAILS */}
  {allGeneration.length > 0 && (
    <div
      className="
        block sm:hidden 
        absolute left-0 right-0 bottom-0 
        h-20
        border-t border-gray-200 
        bg-white/85 backdrop-blur 
        p-2
      "
    >
      <p className="text-xs font-semibold text-gray-900 mb-1">
        {t("images")} :
      </p>

      <div className="flex gap-2 overflow-x-auto">
        {allGeneration.map((img:any, index:any) => (
          <button
            key={index}
            type="button"
            onClick={() => setActiveImage(img)}
            className={`relative h-12 w-12 flex-shrink-0 rounded-md overflow-hidden border-2 transition ${
              activeImage === img
                ? "border-purple-600 shadow-md"
                : "border-gray-200 hover:border-gray-300"
            }`}
            aria-label={`Select generation ${index + 1}`}
          >
            <img
              src={img}
              alt={`Generation ${index + 1}`}
              className="h-full w-full object-cover"
            />
          </button>
        ))}
      </div>
    </div>
  )}
</>

                ) : (
                  <div className="h-full w-full flex flex-col items-center justify-center text-center p-8">
                    <div className="h-12 w-12 rounded-md bg-purple-600/10 flex items-center justify-center mb-3">
                      <span className="text-purple-700 text-xl">🖼️</span>
                    </div>
                    <p className="text-sm font-medium text-gray-900">
                      {t("no_image_selected_yet")}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      {t("generate_image_or_select_previous")}
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Right: Controls - Adjusted for mobile */}
            <div className="min-h-0 overflow-auto border-t lg:border-t-0 lg:border-l border-gray-200 bg-white p-3 sm:p-6 sm:pb-6 h-full">
              {/* Previous Generations */}
              {allGeneration.length > 0 && (
                <div className="hidden sm:block rounded-md border border-gray-200 p-4">
                  <div className="flex items-center justify-between mb-3">
                    <p className="text-sm font-semibold text-gray-900">
                      {t("images")}
                    </p>
                    <p className="text-xs text-gray-500">
                      {t("tap_to_preview")}
                    </p>
                  </div>
                  <div className="flex gap-2 overflow-x-auto pb-2">
                    {allGeneration.map((img: string, index: number) => {
                      return (
                        <button
                          key={index}
                          type="button"
                          onClick={() => {
                            setActiveImage(img);
                          }}
                          className={`group relative h-16 w-16 flex-shrink-0 rounded-md overflow-hidden border-2 transition ${
                            activeImage === img
                              ? "border-purple-600 shadow-md"
                              : "border-gray-200 hover:border-gray-300"
                          }`}
                          aria-label={`Select generation ${index + 1}`}
                          title={`Generation ${index + 1}`}
                        >
                          <img
                            src={img}
                            className="h-full w-full object-cover"
                            alt={`Generation ${index + 1}`}
                          />
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              <div className="flex flex-col gap-3 sm:gap-5 pt-2 sm:pt-4 sm:mb-4">
                {/* Mode Selection */}
                <label className="flex items-start gap-3 cursor-pointer rounded-md border border-gray-200 md:p-4 p-2 hover:bg-gray-50 transition">
                  <input
                    type="checkbox"
                    checked={modifyMode}
                    onChange={handleModifyToggle}
                    className="mt-1 h-4 w-4 text-blue-600 rounded"
                  />
                  <div>
                    <p className="text-sm font-semibold text-gray-900">
                      {t("modify_selected_image_label")}
                    </p>
                    <p className="text-xs text-gray-500 mt-0.5">
                      {t("uncheck_to_generate_new")}
                    </p>
                  </div>
                </label>

                {/* Prompt */}
                <div>
                  <label className="block text-sm font-medium text-gray-900 mb-2">
                    {t("prompt")}
                  </label>
                  <textarea
                    value={prompt}
                    onChange={(e) => setPrompt(e.target.value)}
                    onKeyDown={handleKeyPress}
                    rows={2}
                    placeholder={
                      modifyMode
                        ? t("describe_changes_to_make")
                        : t("describe_image_to_create")
                    }
                    className="w-full resize-none rounded-md border border-gray-300 px-3 py-3 placeholder:text-sm md:text-sm text-xs focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  <div className="mb-2 mt-2 flex items-center justify-between text-xs text-gray-500">
                    <span>{t("be_specific_prompt_tip")}</span>
                    <span>{prompt?.length ?? 0}</span>
                  </div>
                </div>

                {/* Desktop actions - Hidden on mobile */}
                <div className="hidden sm:flex gap-3 pt-1 justify-between">
                  <button
                    onClick={handleSubmit}
                    disabled={
                      isLoading ||
                      !prompt?.trim() ||
                      user?.wallet?.coins + user?.wallet?.referralCoin < 6
                    }
                    className="group w-full rounded-md hover:bg-[#d7d7fc] border border-purple-600 flex items-center justify-between text-base font-semibold text-[#7650e3] transition-colors duration-200 py-2.5 px-4 disabled:opacity-50 disabled:cursor-not-allowed text-md"
                  >
                    {isLoading
                      ? t("generating")
                      : modifyMode
                      ? t("modify")
                      : t("regenerate")}
                    <div className="px-2.5 flex items-center gap-2">
                      <Icon name="spiral-logo" size={20} />
                      <span>{generationAmounts}</span>
                    </div>
                  </button>

                  {user?.wallet?.coins + user?.wallet?.referralCoin < 6 ? (
                    <Link
                      to="/pricing"
                      className="px-4 group rounded-md w-full text-base font-semibold py-2.5 text-purple-700 border border-[#7650e3] theme-bg-trinary theme-text-light hover:bg-[#d7d7fc] hover:text-[#7650e3] disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <div className="flex items-center">
                        <Icon name="white-diamond" size={20} className="mr-2" />
                        {t("upgrade")}
                      </div>
                    </Link>
                  ) : (
                    <button
                      onClick={() => {
                        selectedFile ? onFileSave() : confirmImage(activeImage);
                      }}
                      disabled={
                        isLoading ||
                        !activeImage ||
                        user?.wallet?.coins + user?.wallet?.referralCoin < 6
                      }
                      className="px-4 rounded-md w-full text-base font-semibold py-2.5 text-purple-700 border border-[#7650e3] theme-bg-trinary theme-text-light hover:bg-[#d7d7fc] hover:text-[#7650e3] disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {t("continue")}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Mobile bottom action bar - Fixed position */}
        <div className="sm:hidden w-full  bg-white p-3 z-[60] ">
          <div className="flex gap-2">
            <button
              onClick={handleSubmit}
              disabled={
                isLoading ||
                !prompt?.trim() ||
                user?.wallet?.coins + user?.wallet?.referralCoin < 6
              }
              className="flex-1 py-3 rounded-md border font-semibold border-purple-600 hover:bg-gray-50 transition flex items-center justify-between text-[#7650e3] px-3 disabled:opacity-50 disabled:cursor-not-allowed text-base"
            >
              <span>
                {isLoading
                  ? t("generating")
                  : modifyMode
                  ? t("modify")
                  : t("regenerate")}
              </span>
              <div className="flex items-center gap-2">
                <Icon name="spiral-logo" size={16} />
                <span>{generationAmounts}</span>
              </div>
            </button>

            {user?.wallet?.coins + user?.wallet?.referralCoin < 6 ? (
              <Link
                to="/pricing"
                className="flex-1 py-3 rounded-md text-base px-3 bg-purple-600/10 font-semibold text-purple-700 hover:bg-[#d7d7fc] border border-[#7650e3] flex items-center justify-center transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <div className="flex items-center">
                  <Icon name="white-diamond" size={16} className="mr-2" />
                  {t("upgrade")}
                </div>
              </Link>
            ) : (
              <button
                onClick={() => {
                  selectedFile ? onFileSave() : confirmImage(activeImage);
                }}
                disabled={
                  isLoading ||
                  !activeImage ||
                  user?.wallet?.coins + user?.wallet?.referralCoin < 6
                }
                className="flex-1 py-3 rounded-md text-base px-3 bg-purple-600/10 font-semibold text-purple-700 hover:bg-[#d7d7fc] border border-[#7650e3] transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {t("continue")}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}